import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon as MuiListItemIcon,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Add, Refresh, Edit, Delete, Visibility, VisibilityOff, MoreVert, List as ListIcon, TableChart, Search } from '@mui/icons-material';
import { getCategories, deleteCategory } from '../../services/Api';
import { useNavigate } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination and Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedCategory, setMenuSelectedCategory] = useState(null);

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const navigate = useNavigate();

    // Debounce effect: update debouncedSearch after 1s of no typing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0); // Reset to first page on search
        }, 1000);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchCategories = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getCategories(page + 1, rowsPerPage, debouncedSearch);
            if (response.success) {
                setCategories(response.data || []);
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.total || response.data?.length || 0);
            } else {
                setError(response?.message || 'Failed to fetch categories');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching categories');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch]);

    const handleMenuOpen = (event, category) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedCategory(category);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedCategory(null);
    };

    const handleDeleteClick = () => {
        setSelectedCategory(menuSelectedCategory);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const response = await deleteCategory(selectedCategory.id);
            if (response.success) {
                setDeleteDialogOpen(false);
                fetchCategories();
            } else {
                console.error('Failed to delete category');
            }
        } catch (err) {
            console.error('An error occurred while deleting the category:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300 }}>
                        Category Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your product categories with advanced filtering and nested items
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Search category..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: 250,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <IconButton onClick={fetchCategories} disabled={loading} sx={{ color: 'primary.main' }}>
                        <Refresh />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/categories/modify')}
                        sx={{ px: 4, backgroundColor: 'primary.main',width:200, whiteSpace: 'nowrap' }}
                    >
                        Create Category
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="categories table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Locations</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : categories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">
                                            {debouncedSearch ? `No categories matching "${debouncedSearch}"` : "No categories found"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                categories.map((category) => (
                                    <TableRow key={category.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>
                                            <Avatar
                                                variant="rounded"
                                                src={category.image}
                                                sx={{ width: 50, height: 50, border: '1px solid', borderColor: 'text.border' }}
                                            >
                                                <TableChart />
                                            </Avatar>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={category.type}
                                                size="small"
                                                sx={{
                                                    backgroundColor: category.type === 'Grocery' ? 'success.main' : 'secondary.main',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{category.order}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220 }}>
                                                {category.locations && category.locations.length > 0 ? (
                                                    category.locations.map((loc, idx) => (
                                                        <Chip key={loc.id || idx} label={loc.name} size="small" variant="outlined" sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }} />
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={category.isActive ? <Visibility /> : <VisibilityOff />}
                                                label={category.isActive ? "Active" : "Inactive"}
                                                size="small"
                                                sx={{
                                                    backgroundColor: category.isActive ? 'primary.main' : 'grey.400',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    '& .MuiChip-icon': { color: 'white', fontSize: 16 }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, category)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 180,
                        border: '1px solid',
                        borderColor: 'text.border',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => {
                    navigate(`/categories/modify/${menuSelectedCategory.id}`, { state: { category: menuSelectedCategory } });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
                    Edit
                </MenuItem>
                {menuSelectedCategory?.type?.toLowerCase() === 'grocery' && (
                    <MenuItem onClick={() => {
                        navigate(`/categories/${menuSelectedCategory.id}/items`, { state: { categoryName: menuSelectedCategory.name } });
                        handleMenuClose();
                    }}>
                        <MuiListItemIcon><ListIcon fontSize="small" sx={{ color: 'secondary.main' }} /></MuiListItemIcon>
                        Show Subcategories
                    </MenuItem>
                )}
                <MenuItem onClick={handleDeleteClick}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Category?"
                message={`Are you sure you want to delete "${selectedCategory?.name}"? This action cannot be undone.`}
            />
        </Box>
    );
};

export default GetAllCategories;
